import { appState } from "../../utils/store";
import type { LineProps } from "../coreComponents/types";
import type { SCNode } from "./fsc.types";

export type LineNodeWithMetaData = {
  props: LineProps;
  serializablePropsFP: string;
  key: string;
};

export function createLineRenderManager() {
  let lastMap: Map<string, LineNodeWithMetaData> | null = null;

  function traverse(lines: SCNode<LineProps>[]) {
    const added = new Set<string>();
    const existing = new Set<string>();
    const updated = new Set<string>();
    const keyMap = new Map<string, LineNodeWithMetaData>();
    let isFirstRender = false;

    if (!lastMap) {
      isFirstRender = true;
    }

    const lastMapKeys = new Set(lastMap?.keys() || []);
    const shouldShowStepFlowLines: boolean =
      appState.shouldShowStepFlowLines ?? false;
    lines.forEach(line => {
      const lineWithMetaData = addMetaData(line, shouldShowStepFlowLines);
      keyMap.set(line.key, lineWithMetaData);
      const lastHashedLine = lastMap?.get(line.key);

      if (lastHashedLine) {
        const changes = getChanges({
          lastLine: lastHashedLine,
          currentLine: lineWithMetaData
        });

        if (changes.needsToUpdate) {
          updated.add(line.key);
        }
        existing.add(line.key);
        lastMapKeys.delete(line.key);
      } else {
        added.add(line.key);
      }
    });

    lastMap = keyMap;

    return {
      firstRender: isFirstRender,
      added,
      existing,
      updated,
      removed: lastMapKeys,
      keyMap
    };
  }

  function destroy() {
    lastMap?.clear();
  }

  return {
    getLineForKey: (key: string) => lastMap?.get(key),
    traverse,
    destroy
  };
}

function addMetaData(
  line: SCNode<LineProps>,
  shouldShowStepFlowLines: boolean
): LineNodeWithMetaData {
  return {
    props: line.props,
    serializablePropsFP: generateLinePropsHash(
      line.props,
      shouldShowStepFlowLines
    ),
    key: line.key
  };
}

function getChanges({
  lastLine,
  currentLine
}: {
  lastLine: LineNodeWithMetaData;
  currentLine: LineNodeWithMetaData;
}) {
  const doesNeedsToUpdate =
    lastLine.serializablePropsFP !== currentLine.serializablePropsFP;

  return {
    needsToUpdate: doesNeedsToUpdate
  };
}

function generateLinePropsHash(
  props: LineProps,
  shouldShowStepFlowLines: boolean
): string {
  let serializablePropsFP = `${props.startNodeHash},${props.endNodeHash},`;
  serializablePropsFP += `${props.style.color ?? "-"},`;
  serializablePropsFP += `${props.style.width ?? "-"},`;
  serializablePropsFP += `${props.style.visible ?? "-"},`;
  serializablePropsFP += `${props.isHovered ? "1" : "0"},`;
  serializablePropsFP += `${props.isSelected ? "1" : "0"},`;
  serializablePropsFP += `${shouldShowStepFlowLines ? "1" : "0"}`;

  return serializablePropsFP;
}
